import { cn } from '@goaltree/ui/lib/utils'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LinkifiedText } from '@/components/linkified-text'
import { SavedHint, useSavedHint } from '@/components/saved-hint'

/** Muted multi-line note under a task title. Autosaves debounced and on blur; empty clears it.
 *  Remount (key) per task. */
export function TaskNoteField({
  value,
  onSave,
  onChange,
  disabled,
}: {
  value: string | null | undefined
  onSave: (note: string | null) => Promise<unknown> | void
  onChange?: (note: string | null) => void
  disabled?: boolean
}) {
  const { t } = useTranslation()
  const [text, setText] = useState(value ?? '')
  const [editing, setEditing] = useState(false)
  const ref = useRef<HTMLTextAreaElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const saved = useRef(value ?? '')
  const hint = useSavedHint()

  function flush(val: string) {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    const next = val.trim()
    if (next === saved.current) return
    const prev = saved.current
    saved.current = next
    setText(next)
    onChange?.(next || null)
    void hint.track(onSave(next || null)).then((ok) => {
      if (!ok) saved.current = prev
    })
  }

  function fit() {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }

  useEffect(() => {
    if (!editing) return
    const el = ref.current
    if (!el) return
    el.focus()
    el.setSelectionRange(el.value.length, el.value.length)
    fit()
  }, [editing])

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  if (editing) {
    return (
      <>
        <textarea
          ref={ref}
          rows={1}
          value={text}
          placeholder={t('common.addNote')}
          onChange={(e) => {
            const val = e.target.value
            setText(val)
            fit()
            if (timer.current) clearTimeout(timer.current)
            timer.current = setTimeout(() => flush(val), 500)
          }}
          onBlur={() => {
            flush(text)
            setEditing(false)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') e.currentTarget.blur()
          }}
          className="w-full resize-none bg-transparent text-sm leading-relaxed text-muted-foreground outline-none placeholder:text-muted-foreground/50 [overflow-wrap:anywhere]"
        />
        <SavedHint status={hint.status} />
      </>
    )
  }

  if (disabled && !text) return null
  return (
    <>
      <p
        onClick={() => !disabled && setEditing(true)}
        className={cn(
          'whitespace-pre-wrap text-sm leading-relaxed [overflow-wrap:anywhere]',
          text ? 'text-muted-foreground' : 'text-muted-foreground/50',
          !disabled && 'cursor-text',
        )}
      >
        {text ? <LinkifiedText text={text} /> : t('common.addNote')}
      </p>
      <SavedHint status={hint.status} />
    </>
  )
}
